import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../components/elements/button';
import { Lab } from '../../assets/icons';
import '../../assets/css/lab/cases.css';
import { useNavigate } from 'react-router-dom';
import { casesApi } from '../../network/apis/cases';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const LabCases = () => {
    const navigate = useNavigate();
    const [searchTerm] = useState('');
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilters, setActiveFilters] = useState(0);
    
    const [filters, setFilters] = useState({
        status: 'all',
        priority: 'all',
        testType: 'all'
    });

    const clearFilters = () => {
        setFilters({
            status: 'all',
            priority: 'all',
            testType: 'all'
        });
    };

    useEffect(() => {
        fetchCases();
    }, []);

    useEffect(() => {
        const activeCount = Object.values(filters).filter(value => value !== 'all').length;
        setActiveFilters(activeCount);
    }, [filters]);

    const fetchCases = async () => {
        try {
            setLoading(true);
            const response = await casesApi.getCases();
            
            if (response.data.data) {
                // setCases(response.data.data);
                setCases(
                    [
                        {
                            id: 1,
                            title: 'Water Quality Analysis',
                            description: 'Testing water samples from Farm A',
                            farm: { name: 'Farm A' },
                            status: 'Pending',
                            priority: 'High',
                        }
                    ]
                )
            } else {
                setCases([]);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch cases');
            setCases([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCaseClick = (caseId) => {
        navigate(`/lab/cases/${caseId}`, { state: { case: cases.find(c => c.id === caseId) } });
    };

    // Filter cases based on search and filters
    const filteredCases = cases.filter(item => {
        const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.farm?.name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filters.status === 'all' || item.status?.toLowerCase() === filters.status;
        const matchesPriority = filters.priority === 'all' || item.priority?.toLowerCase() === filters.priority;
        
        return matchesSearch && matchesStatus && matchesPriority;
    });

    const CasesTable = ({ cases: casesList, onCaseClick }) => {
        if (casesList.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-icon">
                        <img src={Lab} alt="" />
                    </div>
                    <h3>No Cases Found</h3>
                    <p>There are no cases matching your criteria.</p>
                    {activeFilters > 0 && (
                        <Button 
                            text="Clear Filters" 
                            variant="outline" 
                            onClick={clearFilters}
                        />
                    )}
                </div>
            );
        }

        return (
            <div className="cases-table-container">
                <table className="cases-table">
                    <thead>
                        <tr>
                            <th>Case ID</th>
                            <th>Title / Farm</th>
                            <th>Status</th>
                            <th>Priority</th>
                            <th>Lab Status</th>
                            <th>Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {casesList.map(caseItem => (
                            <tr 
                                key={caseItem.id} 
                                onClick={() => onCaseClick(caseItem.id)}
                                className={`priority-row-${caseItem.priority?.toLowerCase()}`}
                            >
                                <td className="case-id">#{caseItem.id}</td>
                                <td className="case-title">
                                    <div>
                                        <h4>{caseItem.title}</h4>
                                        <p>{caseItem.farm?.name || 'Unknown Farm'}</p>
                                    </div>
                                </td>
                                <td>
                                    <span className={`status-badge ${caseItem.status?.toLowerCase()}`}>
                                        {caseItem.status}
                                    </span>
                                </td>
                                <td>
                                    <span className={`priority-badge ${caseItem.priority?.toLowerCase()}`}>
                                        {caseItem.priority}
                                    </span>
                                </td>
                                <td>
                                    <span className={`lab-badge ${caseItem.labResults?.toLowerCase() === 'pending' ? 'pending' : 'completed'}`}>
                                        {caseItem.labResults || 'Pending'}
                                    </span>
                                </td>
                                <td className="case-date">
                                    {new Date(caseItem.created_at).toLocaleDateString()}
                                </td>
                                <td>
                                    <Button
                                        text="Process"
                                        variant="primary"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCaseClick(caseItem.id);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    CasesTable.propTypes = {
        cases: PropTypes.array.isRequired,
        onCaseClick: PropTypes.func.isRequired
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Something went wrong</h2>
                <p>{error}</p>
                <Button 
                    text="Try Again" 
                    variant="primary" 
                    onClick={fetchCases}
                />
            </div>
        );
    }

    return (
        <div className="lab-cases">
            <div className="page-header">
                <div className="header-content">
                    <h1>Lab Cases</h1>
                    <p>Manage and process assigned laboratory cases</p>
                </div>
            </div>

            {/* Search and Filters */}
            {/* <div className="filters-section">
                <div className={`search-bar ${isSearchFocused ? 'focused' : ''}`}>
                    <img src={Search} className="search-icon" alt="search" />
                    <input
                        type="text"
                        placeholder="Search by case title, farm name..."
                        onChange={handleSearch}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => setIsSearchFocused(false)}
                    />
                    {searchTerm && (
                        <button 
                            className="clear-search" 
                            onClick={() => setSearchTerm('')}
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="filters-group">
                    <div className="filters-header">
                        <img src={Filter} alt="" className="filter-icon" />
                        <span>Filters</span>
                        {activeFilters > 0 && (
                            <span className="filter-count">{activeFilters}</span>
                        )}
                    </div>

                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                        className={filters.status !== 'all' ? 'active' : ''}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="resolved">Resolved</option>
                    </select>

                    <select
                        value={filters.priority}
                        onChange={(e) => setFilters({...filters, priority: e.target.value})}
                        className={filters.priority !== 'all' ? 'active' : ''}
                    >
                        <option value="all">All Priority</option>
                        <option value="high">High Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="low">Low Priority</option>
                    </select>

                    {activeFilters > 0 && (
                        <button 
                            className="clear-filters"
                            onClick={clearFilters}
                        >
                            Clear All
                        </button>
                    )}
                </div>
            </div> */}

            {/* Results Count */}
            {/* <div className="results-info">
                <span>Showing {filteredCases.length} of {cases.length} cases</span>
            </div> */}

            {/* Cases Table */}
            <CasesTable 
                cases={filteredCases} 
                onCaseClick={handleCaseClick}
            />
        </div>
    );
};

export default LabCases;